/**
 * Suppress noisy, known-false-positive console.error warnings that come from
 * upstream libraries we don't control. Only suppress warnings that we have a
 * specific reason to ignore — every other warning still surfaces normally.
 *
 * Imported for its side-effect from app/_layout.tsx so the filter is in place
 * before any component renders.
 *
 * ── react-native-svg responder props on web ──────────────────────────────────
 *   react-native-svg passes its native-responder handlers
 *   (onStartShouldSetResponder, onResponderRelease, etc.) through to the raw
 *   DOM <g>/<path> elements on web. React DOM doesn't recognize these as event
 *   handlers and emits one console.error per unknown handler, per render. The
 *   touch behavior works fine (clicks are also bound to onClick), but the dev
 *   overlay aggregates these into a noisy red badge. See:
 *     react-native-svg/lib/module/web/utils/prepare.js — spreads the responder
 *     handlers into the props that go to React.createElement on a raw SVG tag.
 *   Suppress until upstream maps these for SVG elements or drops them on web.
 *
 * ── react-native-web pointerEvents deprecation ───────────────────────────────
 *   "props.pointerEvents is deprecated. Use style.pointerEvents" — emitted by
 *   react-native-web on any <View pointerEvents="…">, which expo-router /
 *   react-native-screens still emit internally. Nothing we can fix in app
 *   code; suppress until the upstream packages migrate.
 */

const RESPONDER_PROPS = [
  'onStartShouldSetResponder',
  'onResponderTerminationRequest',
  'onResponderGrant',
  'onResponderMove',
  'onResponderRelease',
  'onResponderTerminate',
]

function joinArgs(args: unknown[]): string {
  // React calls console.error with a `%s`-style format string and the
  // substitutions as separate arguments, e.g.
  //   console.error('Unknown event handler property `%s`...', 'onResponderTerminate')
  // Stringify each arg and concatenate so we can match both halves.
  let out = ''
  for (const a of args) {
    if (typeof a === 'string') out += a
    else if (a != null) {
      try { out += String(a) } catch { /* ignore unprintable */ }
    }
    out += ' '
  }
  return out
}

function isSuppressed(args: unknown[]): boolean {
  const joined = joinArgs(args)
  if (
    joined.includes('Unknown event handler property') &&
    RESPONDER_PROPS.some((p) => joined.includes(p))
  ) {
    return true
  }
  if (joined.includes('props.pointerEvents is deprecated')) {
    return true
  }
  return false
}

if (typeof console !== 'undefined') {
  const origError = console.error
  console.error = (...args: unknown[]) => {
    if (isSuppressed(args)) return
    origError.apply(console, args as Parameters<typeof console.error>)
  }
  const origWarn = console.warn
  console.warn = (...args: unknown[]) => {
    if (isSuppressed(args)) return
    origWarn.apply(console, args as Parameters<typeof console.warn>)
  }
}

export {}
